/**
 * The app-wide "first sound has happened" signal (job #828).
 *
 * Tom's ruling r-2026-09-23: nothing walks the device's saved stores before the
 * first sound, in any mode. On Safari an origin's IndexedDB requests are served
 * one after another, so a walk merely RUNNING holds up the few reads the first
 * sound needs. Background walks that live outside the player (the offline
 * lease refresh reads every saved course script) wait on this.
 *
 * The player marks it when its first-sound gate opens: a lesson clip sounded,
 * or its own fallback fired once ready with no press. A page with no player
 * never marks it, so the waiters run after a fallback delay, unless a player
 * has said it is booting, in which case they wait for that player.
 */
const NO_PLAYER_FALLBACK_MS = 30_000

let firedBy: string | null = null
let playerBooting = false
let fallbackTimer: ReturnType<typeof setTimeout> | null = null
let waiters: Array<() => void> = []

export function markFirstSound(reason: string): void {
  if (firedBy !== null) return
  firedBy = reason
  if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null }
  const run = waiters
  waiters = []
  for (const w of run) w()
}

/** A player is booting: hold the waiters for its first sound, not the fallback. */
export function holdForPlayer(): void {
  playerBooting = true
}

/** Resolves once the first sound has happened (at once if it already has). */
export function firstSound(): Promise<void> {
  if (firedBy !== null) return Promise.resolve()
  if (!fallbackTimer && typeof setTimeout !== 'undefined') {
    fallbackTimer = setTimeout(() => {
      fallbackTimer = null
      if (!playerBooting) markFirstSound('fallback: no player on this page')
    }, NO_PLAYER_FALLBACK_MS)
  }
  return new Promise((resolve) => { waiters.push(resolve) })
}

export function firstSoundFiredBy(): string | null {
  return firedBy
}
